/* eslint-disable quotes */
/* eslint-disable quote-props */
import axios from 'axios';
import { LocalStorage } from 'common/bridges/local-storage.bridge';
import AuthStore from 'common/stores/auth.store';
import {
  BackendError,
  captureError,
  InfrastructureError,
  HTTPError,
} from 'common/utils/error.util';
import { log } from 'common/utils/logging.util';
import Cookies from 'js-cookie';
import { PSP_LIST } from '../common/constants/payments.constants';

const cachedAnswers = LocalStorage.get('cachedAnswers') || {};

export function cachedGet(axiosInstance, url) {
  if (!cachedAnswers[url]) {
    return axiosInstance.get(url).then((resp) => {
      cachedAnswers[url] = resp;
      LocalStorage.set('cachedAnswers', cachedAnswers);
      return resp;
    });
  }
  return Promise.resolve(cachedAnswers[url]);
}

const requestInterceptors = (config = {}) => {
  // Do something before request is sent
  const headers = config.headers || {};

  const authToken = Cookies.get('exprowhite_trading');

  if (authToken) {
    headers['X-Authorization'] = authToken;
  }

  return config;
};

const goToLogin = () => {
  log('smth wrong w/session, go to login page', 'warning');
  AuthStore.initAuth({ isForced: true });
  window.location = '/login';
};

const responseSucessInterceptors = (response) => response.data;
const responseErrorInterceptors = (error) => {
  if (error.response) {
    const AUTH_ERROR_CODES = [401, 403];
    if (
      !AUTH_ERROR_CODES.includes(error.response.status) &&
      error.response.status >= 499
    ) {
      captureError(new BackendError(error), !process.env.REACT_APP_IS_LOCAL);
    }
  }
  if (typeof error.response === 'undefined') {
    captureError(
      new InfrastructureError(
        `There is a CORS error or some problems with client network, url: ${error.request.__sentry_xhr__.url}`
      ),
      !process.env.REACT_APP_IS_LOCAL
    );
  }
  if (error.message === 'timeoutError') {
    captureError(
      new InfrastructureError(error),
      !process.env.REACT_APP_IS_LOCAL
    );
  }
  const httpError = new HTTPError(
    error?.response?.data?.error ||
      error?.response?.data ||
      error?.response.status ||
      error,
    error.config?.url,
    error.config?.method,
    error.config?.params,
    error.response?.status
  );
  return Promise.reject(httpError);
};

const profileResponseErrorInterceptor = (error) => {
  if (error.response?.status === 404 && error.response.data.includes('session'))
    goToLogin();
  responseErrorInterceptors(error);
};

const addInterceptors = (instance) => {
  instance.interceptors.request.use(requestInterceptors);
  instance.interceptors.response.use(
    responseSucessInterceptors,
    responseErrorInterceptors
  );
};

axios.defaults.headers.Accept = 'application/json';
axios.defaults.headers['Content-Type'] = 'application/json';
axios.defaults.timeoutErrorMessage = 'timeoutError';

export const PricingService = axios.create({
  baseURL: `${process.env.REACT_APP_PRICE_BASE_URL}`,
});

const AnalyticsService = axios.create({
  baseURL: process.env.REACT_APP_ANALYTICS_BASE_URL,
});

addInterceptors(PricingService);

export const TradingService = axios.create({
  baseURL: `https://${process.env.REACT_APP_FXTR_BASE_URL}`,
});

addInterceptors(TradingService);

const ContentService = axios.create();
addInterceptors(ContentService);

const RegService = axios.create({
  baseURL: process.env.REACT_APP_REGI_URL,
});

addInterceptors(RegService);

const ProfileService = axios.create({
  baseURL: process.env.REACT_APP_PRES_BASE_URL,
});

addInterceptors(ProfileService);
ProfileService.interceptors.response.use(
  responseSucessInterceptors,
  profileResponseErrorInterceptor
);

export const SignalsClient = axios.create({
  baseURL: `${process.env.REACT_APP_SIGNALS_BASE_URL}`,
});

addInterceptors(SignalsClient);

export const PricingApi = {
  getCandles: (symbol, timeFrame, { from, to } = {}) => {
    let url = `/api/v1/pricing/candles/${symbol}/${timeFrame}`;

    if (from && !to) {
      url += `?from=${from}`;
    } else if (from && to) {
      url += `?from=${from}&to=${to}`;
    }

    return PricingService.get(url);
  },
  getQuotes: (symbol, { from, to } = {}) => {
    let url = `/api/v1/pricing/quotes/${symbol}`;

    if (from && !to) {
      url += `?from=${from}`;
    } else if (from && to) {
      url += `?from=${from}&to=${to}`;
    }

    return PricingService.get(url);
  },
  getInstruments: () => PricingService.get('/api/v1/pricing/instruments'),
};

export const ContentApi = {
  getTree: () => ContentService.get('/content/tree.json'),
  getAllContent: () => ContentService.get('/content/allContent.json'),
  getLanguages: () => ContentService.get('/content/language.json'),
  getContentTranslation: (lang) =>
    ContentService.get(`/content/${lang}/translation.json`),
};

export const getProfileApiUrl = () =>
  `/api/v1/scpr/${process.env.REACT_APP_ID}`;

export const getImageUrl = (guid) => {
  if (guid) {
    return `${
      process.env.REACT_APP_PRESSINVEST_API_URL
    }${getProfileApiUrl()}/media/${guid}/download`;
  }
  return '';
};

export const ProfileApi = {
  levelUpKyc: () =>
    TradingService.put(`/api/v1/fxtr/kyc/level/up/${PSP_LIST.FPF.id}`),
  getKyc: () => TradingService.get(`/api/v1/fxtr/kyc/token/${PSP_LIST.FPF.id}`),
  updateProfile: (profile) =>
    ProfileService.put(`${getProfileApiUrl()}/profile`, profile),
  uploadImage: (file) => {
    const data = new FormData();
    const authToken = Cookies.get('exprowhite_trading');
    data.append('storage_name', process.env.REACT_APP_PROFILE_IMAGE_NAME);
    data.append('media_name', file);
    return fetch(
      `${process.env.REACT_APP_PRESSINVEST_API_URL}${getProfileApiUrl()}/media`,
      {
        method: 'POST',
        headers: {
          'X-Authorization': authToken,
        },
        body: data,
      }
    ).then((response) => response.json());
  },
};

export const AnalyticsApi = {
  getAnalytics: (frame, group) =>
    AnalyticsService.get(`/analytics/${frame}`, {
      params: { group },
    }),

  getChartData: (symbol, timeFrame, { from, to } = {}) => {
    let url = `/candles/${symbol}/${timeFrame}`;

    if (!from && to) {
      url += `?to=${to}`;
    } else if (from && to) {
      url += `?from=${from}&to=${to}`;
    }
    return AnalyticsService.get(url);
  },
};

export const RegApi = {
  sendEmailVerify: (authToken, email, appGuid = process.env.REACT_APP_ID) =>
    RegService.post(`api/v1/registration/${authToken}/${appGuid}/email`, {
      email,
    }),
  confirmResetPassword: ({ verifyID, code }) =>
    RegService.get(`api/v1/verification/operation/${verifyID}?code=${code}`),
  confirmEmail: ({ verifyID, code }) =>
    RegService.get(`api/v1/verification/email/${verifyID}?code=${code}`),
};

export const BonusAPI = {
  getBonusList: (accountID) =>
    TradingService.get(`api/v1/fxtr/account/${accountID}/bonus`),
  getAllAvailableBonusGuides: () => TradingService.get('api/v1/fxtr/bonus'),
  getBonusDetails: (bonus) => TradingService.get(`api/v1/fxtr/bonus/${bonus}`),
  getBonusState: ({ accountID, bonus }) =>
    TradingService.get(`api/v1/fxtr/account/${accountID}/bonus/${bonus}`),
  initiateBonus: ({ accountID, bonus }) =>
    TradingService.post(`api/v1/fxtr/account/${accountID}/bonus/${bonus}`),
  activateBonus: ({ accountID, bonus, code }) =>
    TradingService.post(
      `api/v1/fxtr/account/${accountID}/bonus/${bonus}/activate${
        code ? `?code=${code}` : ''
      }`
    ),
  deactivateBonus: ({ accountID, bonus }) =>
    TradingService.post(
      `api/v1/fxtr/account/${accountID}/bonus/${bonus}/deactivate`
    ),
};

export const WalletApi = {
  getJwt: () => TradingService.get('fxtr/admin/user/token'),
};

export const MastersLeaderboardAPI = {
  getMastersLeaderboard: ({ limit = 5, offset = 0 }) =>
    TradingService.get(
      `api/v1/fxtr/trading-sessions/leaderboard?limit=${limit}&offset=${offset}`
    ),
};

export const SignalsAPI = {
  fetchAssetTypes: () =>
    SignalsClient.post('', {
      path: '/api/assettypes/',
      method: 'GET',
      body: {},
    }),
  fetchAssets: (assetClassId) =>
    SignalsClient.post('', {
      path: '/api/sentiment/asset/',
      params: {
        assetClassId: String(assetClassId),
      },
      method: 'GET',
      body: {},
    }),

  signalsFXEvents: (tickers, dateEndString, todayDate) =>
    SignalsClient.post('', {
      path: '/api/fxevents/',
      method: 'POST',
      body: {
        StartDate: todayDate,
        EndDate: dateEndString,
        AssetNames: tickers,
        Count: 333,
        FillAssetInfo: true,
        ResponseFormat: 0,
      },
    }),

  fetchSignalsData: (assetClassId, dateEndString, todayDate) =>
    SignalsClient.post('', {
      path: '/api/signalcentrereports/',
      method: 'POST',
      body: {
        AssetClassId: String(assetClassId),
        StartDate: todayDate,
        EndDate: dateEndString,
        IncludeExpired: true,
      },
    }),
};
